import {
  defaultAdjustments,
  adjustmentSchema,
  type Adjustments,
  type SceneNode,
} from "@proof/core";
import type { FormGeneratorProps } from "./FormGenerator";
export type ImageTransformer = (
  assetId: string,
  adjustments: Adjustments,
  signal: AbortSignal,
) => Promise<Blob>;
export type AssetLoader = (
  assetId: string,
  signal: AbortSignal,
) => Promise<Blob>;
export interface FormImageServices {
  removeBackground?: import("./background-remover").BackgroundRemovalService;
  upload?: FormGeneratorProps["upload"];
  assets?: FormGeneratorProps["assets"];
  transform?: ImageTransformer;
  loadAsset?: AssetLoader;
}
export const defaultAssetUrl = (id: string) =>
  `/api/assets/${encodeURIComponent(id)}`;
export function imageNeedsTransform(input?: unknown) {
  const parsed = adjustmentSchema.safeParse(input || {});
  if (!parsed.success) return true;
  const adjustments = parsed.data;
  const defaults = defaultAdjustments();
  const keys = new Set([
    ...Object.keys(defaults),
    ...Object.keys(adjustments),
  ] as (keyof Adjustments)[]);
  return [...keys].some(
    (key) => JSON.stringify(adjustments[key]) !== JSON.stringify(defaults[key]),
  );
}
export function checkedPreviewBlob(blob: Blob): Blob {
  if (
    !(blob instanceof Blob) ||
    !blob.size ||
    blob.size > 20000000 ||
    !["image/png", "image/jpeg", "image/webp"].includes(blob.type)
  )
    throw new Error("IMAGE_PREVIEW_INVALID");
  return blob;
}
export function httpImageTransformer(
  assetUrl = defaultAssetUrl,
): ImageTransformer {
  return async (id, adjustments, signal) => {
    const response = await fetch(`${assetUrl(id)}/transform`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adjustments }),
      signal,
    });
    if (!response.ok) throw new Error("IMAGE_PREVIEW_UNAVAILABLE");
    return checkedPreviewBlob(await response.blob());
  };
}

export function imagePreviewKey(
  node: Pick<SceneNode, "id" | "assetId" | "adjustments">,
) {
  return JSON.stringify([node.id, node.assetId, node.adjustments]);
}
